<script setup lang="ts">
/**
 * /p/<code> — a partner's own page (constants/affiliate.ts). The link they
 * share most: short enough to say aloud or print, and it greets the visitor
 * with the partner's name, their own words and their discount code, with
 * the CV builder right below so nobody has to look for where to start.
 *
 * Arriving here counts as a click on the partner's link: the page puts
 * ?ref=<code> into the address for plugins/affiliate.client.ts, which records
 * it (and the referral cookie, with consent) and drops it again. A ?src= tag
 * from the partner's share buttons is kept for their statistics.
 */
import { normalizeAffiliateCode, normalizeAffiliateSource } from '~/constants/affiliate'

definePageMeta({ layout: false, auth: false })

interface Partner {
  code: string
  name: string | null
  message: string | null
  discountCode: string | null
  discountPercent: number | null
}

const { t } = useI18n()
const { show: showSnackbar } = useSnackbar()
const route = useRoute()
const router = useRouter()
const localize = useLocalizedPath()

const code = normalizeAffiliateCode(String(route.params.code ?? ''))
const { data: partner, error } = await useFetch<Partner>(() => `/api/affiliate/partner/${code}`, {
  key: `partner-${code}`,
  immediate: !!code,
})

// An unknown code or an ended membership: the visitor still wanted us.
if (!code || error.value || !partner.value) {
  await navigateTo(localize('/'), { redirectCode: 302, replace: true })
}

const name = computed(() => partner.value?.name || null)
const initials = computed(() => (name.value ?? '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(word => word[0]!.toUpperCase())
  .join(''))

const title = computed(() => name.value
  ? t('partnerPage.seoTitle', { name: name.value })
  : t('partnerPage.seoTitleAnonymous'))
useSeoMeta({
  title,
  description: () => t('partnerPage.seoDescription'),
  ogTitle: title,
  ogDescription: () => t('partnerPage.seoDescription'),
  robots: 'noindex, follow',
})

async function copyCode() {
  if (!partner.value?.discountCode) return
  try {
    await navigator.clipboard.writeText(partner.value.discountCode)
    showSnackbar(t('partnerPage.codeCopied'))
  } catch {
    // Clipboard blocked: the code is on screen to type.
  }
}

// Present already when the link carried it; the plugin then records it.
const arrivedWithRef = 'ref' in route.query
onMounted(() => {
  if (!code || !partner.value || arrivedWithRef) return
  const source = normalizeAffiliateSource(route.query.src) || 'page'
  router.replace({ query: { ...route.query, ref: code, src: source } }).catch(() => {})
})
</script>

<template>
  <NuxtLayout name="sell" :show-back-button="false">
    <div v-if="partner" class="w-full bg-white dark:bg-gray-950">
      <section class="sofax border-b border-line bg-surface dark:border-gray-800 dark:bg-gray-900">
        <div class="shell py-8 sm:py-10">
          <div class="mx-auto flex max-w-3xl flex-col items-center gap-5 text-center sm:flex-row sm:items-start sm:text-left">
            <div
              class="flex size-16 shrink-0 items-center justify-center rounded-full bg-accent-soft/50 font-title text-2xl font-bold text-ink dark:bg-accent/20 dark:text-accent-soft"
              aria-hidden="true"
            >
              <span v-if="initials">{{ initials }}</span>
              <i v-else class="bi bi-person-heart" />
            </div>
            <div class="min-w-0 flex-1">
              <p class="font-title text-lg font-semibold text-ink dark:text-white">
                {{ name ? t('partnerPage.recommends', { name }) : t('partnerPage.recommendsAnonymous') }}
              </p>
              <blockquote v-if="partner.message" class="mt-2 whitespace-pre-line break-words text-body dark:text-gray-300">
                “{{ partner.message }}”
              </blockquote>
              <p v-else class="mt-2 text-body dark:text-gray-300">{{ t('partnerPage.defaultMessage') }}</p>
              <div v-if="partner.discountCode" class="mt-4 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-accent bg-white px-4 py-2 font-mono text-lg font-bold tracking-wider text-ink transition-colors hover:bg-accent-soft/30 dark:bg-gray-950 dark:text-white"
                  :aria-label="t('partnerPage.copyCode', { code: partner.discountCode })"
                  @click="copyCode"
                >
                  {{ partner.discountCode }}
                  <i class="bi bi-clipboard text-base text-muted" aria-hidden="true" />
                </button>
                <span class="text-sm font-semibold text-ink dark:text-white">{{ t('partnerPage.codeOff', { percent: partner.discountPercent }) }}</span>
              </div>
              <p class="mt-3 text-xs text-muted dark:text-gray-400">
                {{ partner.discountCode ? t('partnerPage.codeHint') : '' }}
                {{ name ? t('partnerPage.disclosure', { name }) : t('partnerPage.disclosureAnonymous') }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <LandingCvCreateModes />

      <SofaxTrustStrip />

      <LazyVisibleSofaxOutputShowcase hydrate-on-visible />
    </div>
  </NuxtLayout>
</template>
